import { useState } from 'react';
import { useOnboardingStore } from '@/store/onboardingStore';
import { TextureButton } from '@/components/ui/TextureButton';
import type { WeeklySchedule } from '@/types/onboarding';

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const BLOCKS = ['morning', 'midday', 'afternoon', 'evening'] as const;

export function ScheduleScreen() {
    const { profile, setSchedule, nextStep } = useOnboardingStore();

    // Initialize empty schedule if none
    const [schedule, setLocalSchedule] = useState<WeeklySchedule>(
        profile.schedule || DAYS.reduce((acc, day) => ({
            ...acc,
            [day]: { morning: false, midday: false, afternoon: false, evening: false }
        }), {} as WeeklySchedule)
    );

    const toggle = (day: typeof DAYS[number], block: typeof BLOCKS[number]) => {
        setLocalSchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [block]: !prev[day][block]
            }
        }));
    };

    const handleContinue = () => {
        setSchedule(schedule);
        nextStep();
    };

    return (
        <div className="px-2 animate-fade-in-right h-[calc(100vh-140px)] flex flex-col">
            <h2 className="text-xl font-bold text-text-primary mb-2">Weekly Schedule</h2>
            <p className="text-text-secondary text-xs mb-4">Tap available training blocks.</p>

            <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-[auto_1fr] gap-4">
                    <div className="space-y-3 pt-8">
                        {BLOCKS.map(b => (
                            <div key={b} className="h-8 flex items-center text-[10px] font-bold text-text-muted uppercase tracking-wider">
                                {b.slice(0, 3)}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {DAYS.map(day => (
                            <div key={day} className="space-y-3">
                                <div className="text-center text-[10px] font-bold text-gold-500 uppercase mb-2">
                                    {day.slice(0, 1).toUpperCase()}
                                </div>
                                {BLOCKS.map(block => {
                                    const active = schedule[day][block];
                                    return (
                                        <button
                                            key={`${day}-${block}`}
                                            onClick={() => toggle(day, block)}
                                            className={`w-full h-8 rounded-md border transition-all ${active
                                                    ? 'bg-gold-500 border-gold-400 shadow-[0_0_8px_rgba(201,162,39,0.4)]'
                                                    : 'bg-white/5 border-white/5 hover:bg-white/10'
                                                }`}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <TextureButton onClick={handleContinue} className="w-full mt-4">
                Analyze Profile
            </TextureButton>
        </div>
    );
}
